import React, { useState, useEffect, useRef } from 'react';
import { User, Building2, Mail, Phone, Loader2, UserPlus } from 'lucide-react';
import { checkEmailExists, extractCustomerDataFromNote } from '../../entities/Notes/emailLookupService';

/**
 * EmailHoverPopover Component
 * Shows customer/user details on hover, or option to create customer if not found
 */
const EmailHoverPopover = ({
  email,
  noteText,
  getProtectedTenantCode,
  onCreateCustomer,
  children
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [extractingData, setExtractingData] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const popoverRef = useRef(null);
  const containerRef = useRef(null);

  // Check email when hover starts
  useEffect(() => {
    if (isVisible && !emailData && !isLoading) {
      setIsLoading(true);
      checkEmailExists(email, getProtectedTenantCode)
        .then(result => {
          setEmailData(result);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error checking email:', error);
          setIsLoading(false);
        });
    }
  }, [isVisible, email, emailData, isLoading, getProtectedTenantCode]);

  // Handle mouse enter with delay
  const handleMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    // Set show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, 300); // 300ms delay before showing
  };

  // Handle mouse leave with delay
  const handleMouseLeave = () => {
    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    
    // Set hide timeout with delay to allow moving to popover
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setExtractedData(null);
      setExtractingData(false);
    }, 200); // 200ms delay before hiding - gives time to move to popover
  };

  // Keep popover visible when hovering over it
  const handlePopoverMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    // Ensure popover stays visible
    setIsVisible(true);
  };

  // Handle mouse leave from popover
  const handlePopoverMouseLeave = () => {
    // Set hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setExtractedData(null);
      setExtractingData(false);
    }, 200);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Handle create customer click
  const handleCreateCustomer = async () => {
    console.log('=== CREATE CUSTOMER CLICKED ===');
    console.log('Email:', email);
    console.log('Extracted data cached:', extractedData);
    
    if (extractedData) {
      console.log('Using cached extracted data');
      onCreateCustomer(email, extractedData, noteText);
      return;
    }

    setExtractingData(true);
    try {
      console.log('Calling extractCustomerDataFromNote...');
      const result = await extractCustomerDataFromNote(noteText, email, getProtectedTenantCode);
      console.log('Extract result:', result);
      
      if (result.success && result.data) {
        console.log('Extraction successful, data:', result.data);
        setExtractedData(result.data);
        onCreateCustomer(email, result.data, noteText);
      } else {
        console.warn('Extraction failed or no data, using email only');
        // Still navigate with basic data
        onCreateCustomer(email, { email }, noteText);
      }
    } catch (error) {
      console.error('Error extracting data:', error);
      // Navigate with basic email data
      onCreateCustomer(email, { email }, noteText);
    } finally {
      setExtractingData(false);
    }
  };

  // Get display name from data
  const getDisplayName = (data) => {
    if (data.type === 'customer') {
      if (data.data.CustomerType === 'Person') {
        const firstName = data.data.FirstName || '';
        const lastName = data.data.LastName || '';
        return `${firstName} ${lastName}`.trim() || 'Customer';
      } else {
        return data.data.CompanyName || 'Company';
      }
    } else if (data.type === 'user') {
      const firstName = data.data.FirstName || '';
      const lastName = data.data.LastName || '';
      return `${firstName} ${lastName}`.trim() || 'User';
    }
    return null;
  };

  // Get phone from data
  const getPhone = (data) => {
    if (data.type === 'customer') {
      return data.data.PersonPhone || data.data.CompanyPhone || '';
    } else if (data.type === 'user') {
      return data.data.Phone || '';
    }
    return '';
  };

  return (
    <span
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      {/* Invisible bridge area to help mouse reach popover */}
      {isVisible && (
        <div
          className="absolute z-40 bottom-full left-1/2 transform -translate-x-1/2 mb-0 w-64 h-2"
          style={{ pointerEvents: 'auto' }}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        />
      )}
      
      {isVisible && (
        <div
          ref={popoverRef}
          className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 animate-fadeIn"
          style={{ minWidth: '256px', pointerEvents: 'auto' }}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          {/* Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-8 border-t-gray-200 dark:border-t-gray-700 border-l-transparent border-r-transparent border-b-transparent" />
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px border-8 border-t-white dark:border-t-gray-800 border-l-transparent border-r-transparent border-b-transparent" />

          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400" />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Checking...</span>
            </div>
          ) : emailData?.found ? (
            <div className="space-y-2">
              <div className="flex items-start space-x-2">
                {emailData.type === 'customer' && emailData.data.CustomerType === 'Company' ? (
                  <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                ) : (
                  <User className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                    {getDisplayName(emailData)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {emailData.type === 'customer' ? 'Customer' : 'User'}
                  </div>
                </div>
              </div>
              
              <div className="space-y-1 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 text-xs">
                  <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600 dark:text-gray-400 truncate">{email}</span>
                </div>
                {getPhone(emailData) && (
                  <div className="flex items-center space-x-2 text-xs">
                    <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-400">{getPhone(emailData)}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {email}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Not found in system
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleCreateCustomer}
                disabled={extractingData}
                className="w-full mt-2 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {extractingData ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Extracting data...</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    <span>Create Customer</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </span>
  );
};

export default EmailHoverPopover;

